/**
 * The first thing the accounting screen shows: what is in every box right
 * now, money in and out over a period by category, and what invoices are
 * still owed. Everything per currency - nothing here adds CNY to USD.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import { FilterDates } from "components/FilterBar/controls";
import { api, problemOf, money, byCurrency } from "./api";

const panelSx = ({ palette, borders, functions: { rgba } }) => ({
  border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
  borderRadius: borders.borderRadius.lg,
  p: 2,
});

function BoxCard({ box }) {
  return (
    <SuiBox sx={panelSx} flex="1 1 220px" maxWidth={320} opacity={box.is_active ? 1 : 0.55}>
      <SuiBox display="flex" alignItems="center" gap={1} mb={1}>
        <Icon sx={{ color: "#C9A227" }}>{box.kind === "cash" ? "savings" : "account_balance"}</Icon>
        <SuiTypography sx={{ fontSize: 14, fontWeight: 700 }}>{box.name}</SuiTypography>
      </SuiBox>
      <SuiTypography
        sx={({ palette }) => ({
          fontSize: 22,
          fontWeight: 800,
          color: box.balance < 0 ? palette.error.main : palette.dark.main,
        })}
      >
        {money(box.balance)}
        <SuiTypography
          component="span"
          sx={{ fontSize: 13, fontWeight: 600, ml: 0.75, opacity: 0.6 }}
        >
          {box.currency}
        </SuiTypography>
      </SuiTypography>
    </SuiBox>
  );
}

BoxCard.propTypes = {
  box: PropTypes.shape({
    name: PropTypes.string,
    kind: PropTypes.string,
    currency: PropTypes.string,
    balance: PropTypes.number,
    is_active: PropTypes.bool,
  }).isRequired,
};

/** One small table: label, currency, amount. */
function MiniTable({ title, rows, empty }) {
  return (
    <SuiBox sx={panelSx} flex="1 1 360px">
      <SuiTypography variant="button" fontWeight="bold" display="block" mb={1}>
        {title}
      </SuiTypography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {rows.head.map((h, i) => (
              <TableCell key={h} align={i === 0 ? "left" : "right"}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.body.map((r) => (
            <TableRow key={r.join("|")}>
              {r.map((v, i) => (
                <TableCell
                  key={rows.head[i]}
                  align={i === 0 ? "left" : "right"}
                  sx={{ fontSize: 13.5, fontWeight: i === r.length - 1 ? 700 : 500 }}
                >
                  {v}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {rows.body.length === 0 && (
            <TableRow>
              <TableCell colSpan={rows.head.length} sx={{ fontSize: 13, opacity: 0.6 }}>
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </SuiBox>
  );
}

MiniTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.shape({
    head: PropTypes.arrayOf(PropTypes.string),
    body: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  }).isRequired,
  empty: PropTypes.string.isRequired,
};

function Overview({ refreshKey }) {
  const month = new Date().toISOString().slice(0, 8);
  const [from, setFrom] = useState(`${month}01`);
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    api
      .get("/summary", { from: from || undefined, to: to || undefined })
      .then(setData)
      .catch((e) => setError(problemOf(e, "Could not load the overview")));
  }, [from, to, refreshKey]);

  if (error) {
    return (
      <SuiBox px={3} py={3}>
        <SuiTypography variant="button" color="error">
          {error}
        </SuiTypography>
      </SuiBox>
    );
  }

  if (!data) return <CargoLoader label="Loading accounts" />;

  const cats = data.by_category || [];
  const inflow = byCurrency(
    cats.filter((c) => c.type === "receipt"),
    (c) => c.total
  );
  const outflow = byCurrency(
    cats.filter((c) => c.type === "payment"),
    (c) => c.total
  );
  (data.supplier_paid || []).forEach((s) => {
    outflow[s.currency] = (outflow[s.currency] || 0) + Number(s.total);
  });
  const currencies = [...new Set([...Object.keys(inflow), ...Object.keys(outflow)])];

  const catRows = (type) =>
    cats
      .filter((c) => c.type === type)
      .map((c) => [c.category || "Uncategorised", c.currency, String(c.n), money(c.total)]);

  return (
    <SuiBox px={3} py={2} display="flex" flexDirection="column" gap={3}>
      <SuiBox>
        <SuiTypography variant="h6" mb={1.5}>
          Balances now · الأرصدة
        </SuiTypography>
        <SuiBox display="flex" gap={2} flexWrap="wrap">
          {data.boxes.map((b) => (
            <BoxCard key={b.id} box={b} />
          ))}
        </SuiBox>
      </SuiBox>

      <SuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <SuiTypography variant="h6">Period · الفترة</SuiTypography>
        <FilterDates label="Dates" from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </SuiBox>

      <MiniTable
        title="Money in vs out · المقبوضات والمدفوعات"
        empty="Nothing in this period"
        rows={{
          head: ["Currency", "In", "Out", "Net"],
          body: currencies.map((c) => [
            c,
            money(inflow[c] || 0),
            money(outflow[c] || 0),
            money((inflow[c] || 0) - (outflow[c] || 0)),
          ]),
        }}
      />

      <SuiBox display="flex" gap={3} flexWrap="wrap">
        <MiniTable
          title="Expenses by category · المصاريف حسب الفئة"
          empty="No payment vouchers in this period"
          rows={{
            head: ["Category", "Currency", "Count", "Total"],
            body: [
              ...catRows("payment"),
              ...(data.supplier_paid || []).map((s) => [
                "Supplier payments (Suppliers screen)",
                s.currency,
                String(s.n),
                money(s.total),
              ]),
            ],
          }}
        />
        <MiniTable
          title="Income by category · الإيرادات حسب الفئة"
          empty="No receipt vouchers in this period"
          rows={{ head: ["Category", "Currency", "Count", "Total"], body: catRows("receipt") }}
        />
      </SuiBox>

      <MiniTable
        title="Invoices in this period · الفواتير"
        empty="No invoices in this period"
        rows={{
          head: ["Type", "Currency", "Count", "Total", "Paid", "Still owed"],
          body: (data.invoices || []).map((i) => [
            i.type === "sales" ? "Sales (owed to us)" : "Purchases (we owe)",
            i.currency,
            String(i.count),
            money(i.total),
            money(i.paid),
            money(i.remaining),
          ]),
        }}
      />
    </SuiBox>
  );
}

Overview.propTypes = { refreshKey: PropTypes.number.isRequired };

export default Overview;
